<template>
  <v-container fluid class="edit-user" v-if="user">
    <v-row class="flex-grow-0">
      <v-btn text x-small color="primary" exact :to="{ name: 'user-list' }">
        &lt;&lt; {{ $t("app_drawer.users") }}
      </v-btn>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>
            {{ user.username }}
            <div :class="['user-status-div', { disabled: user.is_external }]">
              {{ user.is_external ? $t("user.external") : $t("user.internal") }}
            </div>

            <div :class="['user-status-div', { disabled: user.disabled }]">
              {{
                user.disabled
                  ? $t("user.disabled_true")
                  : $t("user.disabled_false")
              }}
              <div class="user-status-bubble"></div>
            </div>

            <v-btn class="ml-4" color="primary" small @click="toggleDisabled">
              {{ user.disabled ? $t("activate") : $t("deactivate") }}
            </v-btn>

            <v-btn
              class="ml-4"
              color="primary"
              small
              @click="resetPassword"
              v-if="user.is_external"
            >
              {{ $t("user.reset_password") }}
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-form ref="form">
              <v-text-field
                outlined
                :label="$t('user.name')"
                v-model="user.name"
                :rules="requiredRules"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.company')"
                v-model="user.company"
                :rules="requiredRules"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.post')"
                v-model="user.post"
                :rules="requiredRules"
              ></v-text-field>
              <v-text-field
                outlined
                :label="$t('user.phone')"
                v-model="user.phone"
                prefix="+852"
              ></v-text-field>
              <v-divider></v-divider>
              <div
                class="d-flex mt-2"
                style="justify-content: start; align-items: center; gap: 1rem"
              >
                <h3 class="">{{ $t("user.permissions") }}</h3>
                <v-btn
                  v-if="isViewerSuperAdmin && !isSuperAdmin && !isSameUser"
                  @click="() => changeUserType(USER_TYPE.SUPER_ADMIN)"
                  color="primary"
                  small
                  >Set to Super Admin</v-btn
                >
                <v-btn
                  v-if="isViewerSuperAdmin && isSuperAdmin && !isSameUser"
                  @click="() => changeUserType(USER_TYPE.USER)"
                  color="primary"
                  small
                  >Remove Super Admin</v-btn
                >
              </div>

              <v-row>
                <div
                  class="mx-4"
                  style="
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    gap: 1rem;
                  "
                >
                  <v-checkbox
                    v-model="isSuperAdmin"
                    disabled
                    :label="$t('user_type.SUPER_ADMIN')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="isAdmin"
                    :disabled="isSuperAdmin"
                    :label="$t('user_type.ADMIN')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    value="esor.manage_sor"
                    :disabled="isSuperAdmin || isAdmin"
                    :label="$t('permissions.esor.manage_sor')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    value="esor.input_sor"
                    :disabled="isSuperAdmin || isAdmin"
                    :label="$t('permissions.esor.input_sor')"
                  ></v-checkbox>
                  <v-checkbox
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="esor.create_contract"
                    :label="$t('permissions.esor.create_contract')"
                  ></v-checkbox>

                  <v-checkbox
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="esor.manage_nsr"
                    :label="$t('permissions.esor.manage_nsr')"
                  ></v-checkbox>

                  <v-checkbox
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="esor.manage_nsr_ind"
                    :label="$t('permissions.esor.manage_nsr_ind')"
                  ></v-checkbox>

                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="rbu.manage_dataset"
                    :label="'Manage Reference DB'"
                  ></v-checkbox>

                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="rbu.manage_edition"
                    :label="$t('permissions.rbu.manage_edition')"
                  ></v-checkbox>

                  <v-checkbox
                    v-if="canEditRBUPermission"
                    v-model="permissions"
                    :disabled="isSuperAdmin || isAdmin"
                    value="rbu.template_verification"
                    :label="$t('permissions.rbu.template_verification')"
                  ></v-checkbox>
                </div>
              </v-row>

              <h3 v-if="canEditRBUPermission" class="mt-4">
                Special Permission
              </h3>
              <v-row v-if="canEditRBUPermission">
                <div
                  class="mx-4"
                  style="
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    gap: 1rem;
                  "
                >
                  <v-checkbox
                    v-model="special_permissions"
                    :value="SPECIAL_PERMISSIONS.ASOR_RBU"
                    :label="'ASoR RBU'"
                  ></v-checkbox>
                </div>
              </v-row>
              <v-divider v-if="canEditRBUPermission"></v-divider>
              <h3 class="mt-4">Contract Admin</h3>
              <v-row>
                <v-col>
                  <UserContractAdminList
                    :user-id="$route.params.userId"
                  ></UserContractAdminList>
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <h3 class="mt-4">Contract User</h3>
              <v-row>
                <v-col>
                  <UserContractUserList
                    :user-id="$route.params.userId"
                  ></UserContractUserList>
                </v-col>
              </v-row>
              <v-divider></v-divider>
              <h3 class="mt-4">RBU User</h3>
              <v-row>
                <v-col>
                  <AdminManagingRbuList
                    :account-id="$route.params.userId"
                  ></AdminManagingRbuList>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="$refs.form.validate() && save()">
              {{ $t("save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ref } from "@vue/composition-api/dist/vue-composition-api";
import { ACTION_TYPES } from "@/store/types";
import { USER_TYPE, PERMISSIONS, SPECIAL_PERMISSIONS } from "@/constants";
import { computed, onMounted } from "@vue/composition-api";
import UserContractAdminList from "@/components/user/UserContractAdminList";
import ResetUserPasswordDialog from "@/components/user/ResetUserPasswordDialog";
import UserContractUserList from "@/components/user/UserContractUserList";
import AdminManagingRbuList from "@/components/user/AdminManagingRbuList";

export default {
  name: "User",
  components: {
    UserContractUserList,
    UserContractAdminList,
    AdminManagingRbuList,
  },
  setup(props, { root }) {
    const canEditRBUPermission = computed(() => {
      return (
        root.$store.getters.account &&
        (["SUPER_ADMIN", "ADMIN"].includes(root.$store.getters.account.type) ||
          root.$store.getters.hasPermission(
            PERMISSIONS.MANAGE_RBU_EDITION,
            true
          ))
      );
    });
    const isSameUser = computed(() => {
      return (
        root.$store.getters.account &&
        root.$store.getters.account.id === root.$route.params.userId
      );
    });
    const isViewerSuperAdmin = computed(() => {
      return (
        root.$store.getters.account &&
        root.$store.getters.account.type === USER_TYPE.SUPER_ADMIN
      );
    });
    const requiredRules = ref([
      (value) => !!value || root.$t("rules.required"),
    ]);
    // const emailReg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    const emailReg = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/;
    const emailRules = ref([
      (value) => emailReg.test(value) || root.$t("rules.email"),
    ]);
    const phoneRules = ref([
      (value) => /^[2456789][0-9]{7}$/.test(value) || root.$t("rules.phone"),
    ]);

    const user = ref(null);
    const isSuperAdmin = ref(false);
    const special_permissions = ref([]);
    const isAdmin = ref(false);
    const permissions = ref([]);

    const getUser = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/user/",
        params: {
          id: root.$route.params.userId,
        },
      });

      if (response.body.success) {
        user.value = response.body.user;
        isSuperAdmin.value = response.body.user.type === USER_TYPE.SUPER_ADMIN;
        isAdmin.value = response.body.user.type === USER_TYPE.ADMIN;
        permissions.value = response.body.user.permissions;
        special_permissions.value = response.body.user.special_permissions;

        if (
          response.body.user &&
          root.$store.getters.account &&
          response.body.user.id === root.$store.getters.account.id
        ) {
          root.$store.dispatch(ACTION_TYPES.GET_ME);
        }
      }
    };

    const toggleDisabled = async function () {
      const url = user.value.disabled
        ? "admin/user/enable/"
        : "admin/user/disable/";
      const confirmMsg = user.value.disabled
        ? "user.enable_confirm_msg"
        : "user.disable_confirm_msg";

      if (
        await root.$dialog.confirm({
          text: root.$t(confirmMsg),
          showClose: false,
        })
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url,
          params: { id: root.$route.params.userId },
        });

        if (response.body.success) {
          root.$showAlertMessage(root.$t("user.disabled_updated"));
          await getUser();
        }
      }
    };

    const resetPassword = async function () {
      await root.$dialog.showAndWait(ResetUserPasswordDialog, {
        showClose: false,
        persistent: true,
        userId: root.$route.params.userId,
      });
    };

    const save = async function () {
      const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
        url: "admin/user/edit/",
        params: {
          ...user.value,
          id: root.$route.params.userId,
          type: isAdmin.value ? USER_TYPE.ADMIN : USER_TYPE.USER,
          permissions: isAdmin.value ? permissions.value : permissions.value,
          special_permissions: special_permissions.value,
        },
      });

      if (response.body.success) {
        root.$showAlertMessage(root.$t("user.disabled_updated"));
        await getUser();
      }
    };

    const changeUserType = async function (targetType) {
      if (
        await root.$dialog.confirm({
          text: `Are you sure you want to change user type to ${targetType}?`,
          showClose: false,
        })
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/user/edit_super_admin/",
          params: {
            id: root.$route.params.userId,
            type: targetType,
          },
        });

        if (response.body.success) {
          root.$showAlertMessage("User Updated");
          await getUser();
        }
      }
    };

    onMounted(() => {
      getUser();
    });

    return {
      requiredRules,
      emailRules,
      phoneRules,
      user,
      isSuperAdmin,
      isAdmin,
      permissions,
      resetPassword,
      save,
      toggleDisabled,
      canEditRBUPermission,
      SPECIAL_PERMISSIONS,
      special_permissions,
      isViewerSuperAdmin,
      changeUserType,
      USER_TYPE,
      isSameUser,
    };
  },
};
</script>

<style lang="less">
.edit-user {
  height: 100%;
  display: flex;
  flex-direction: column;

  .user-status-div {
    margin-left: 8px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    color: #5bc23f;
    background-color: rgba(91, 194, 63, 0.2);
    border-radius: 16px;

    .user-status-bubble {
      margin-left: 8px;
      height: 12px;
      width: 12px;
      background-color: #5bc23f;
      border-radius: 8px;
    }

    &.disabled {
      color: #dddbdb;
      background-color: rgba(221, 219, 219, 0.2);

      .user-status-bubble {
        background-color: #dddbdb;
      }
    }
  }
}
</style>
